import React, { JSX, useContext, useEffect, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { ApolloError } from "@apollo/client";
import { GraphQLError } from "graphql";
import { UserContext } from "context/user";
import { useLoginMutation } from "services/graphql/generated";
import { parseLoginUserGraphQLErrors } from "@pepdirect/v3/helpers/errors";
import { META_TITLE } from "constants/meta";

import st from "./login.module.scss";

// Full-bleed team login screen (no site chrome). Successful login drops the
// backend session cookie; substore scoping is applied server-side from then on.
export default function TeamLogin(): JSX.Element {
  const router = useRouter();
  const { currentUserId, setUser } = useContext(UserContext);
  const [loginMutation, { loading }] = useLoginMutation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (currentUserId) {
      router.replace("/team/shop");
    }
  }, [currentUserId, router]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;
    setErrorMessage("");

    if (!email.trim() || !password) {
      setErrorMessage("Please enter your email address and password.");
      return;
    }

    try {
      const { data } = await loginMutation({
        variables: { email: email.trim(), password },
      });
      setUser(data?.loginUser?.id);
      router.push("/team/shop");
    } catch (err) {
      if (err instanceof ApolloError && err.graphQLErrors.length) {
        const { errorMsg } = parseLoginUserGraphQLErrors(
          err.graphQLErrors as GraphQLError[]
        );
        setErrorMessage(errorMsg || "Invalid email or password.");
      } else {
        setErrorMessage("Something went wrong. Please try again.");
      }
    }
  };

  return (
    <div className={st.page}>
      <Head>
        <title>{`Team login – ${META_TITLE}`}</title>
        <meta name="robots" content="noindex" />
      </Head>

      <div className={st.panel}>
        <span className={st.logo} aria-hidden="true" />
        <h1 className={st.title}>Team login</h1>

        <form className={st.form} onSubmit={onSubmit} noValidate>
          <label className={st.label} htmlFor="team-email">
            email address<span className={st.req}>*</span>
          </label>
          <input
            id="team-email"
            className={st.input}
            type="email"
            name="email"
            placeholder="email address"
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
          />

          <label className={st.label} htmlFor="team-password">
            password<span className={st.req}>*</span>
          </label>
          <input
            id="team-password"
            className={st.input}
            type="password"
            name="password"
            placeholder="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
          />

          <p className={st.terms}>
            By accessing this site and/or placing your order you agree to
            PepsiCo&apos;s terms of service. Please see{" "}
            <Link href="/terms" className={st.termsLink}>
              terms of service
            </Link>{" "}
            for more information.
          </p>

          {errorMessage && (
            <p className={st.error} role="alert">
              {errorMessage}
            </p>
          )}

          <button className={st.submit} type="submit" disabled={loading}>
            {loading ? "logging in…" : "login"}
          </button>
        </form>
      </div>
    </div>
  );
}

TeamLogin.hideChrome = true;
